import React from 'react';
import { ImportSessionStorage } from 'utils/importSessionStorage';
import Footer from 'components/Footer';
import { SEOHelmet, BreadcrumbList } from 'components/SEO';
import { AssetUrls } from 'config/assets';
import { BRAND_GRADIENT } from 'screens/Public/Brand/brandData';
import { TopDeckedMark } from 'components/TopDeckedMark/TopDeckedMark';
import Points from 'components/Points';
import { CardLink } from 'components/CardLink';
import { HERO_CARD_IDS, MATCH_CARD_ID } from 'constants/topdeckedHeroCards';
import './topdecked.css';

// Sign-up destination. The UTM tags identify TopDecked-sourced traffic and
// match the utm_source capture in AnalyticsTracker (src/routes/index.tsx),
// keeping attribution consistent with other partnership links. A full anchor
// navigation (not SPA routing) is intentional so the analytics tracker
// re-reads utm_source on landing.
const SIGNUP_HREF =
  '/welcome?utm_source=topdecked&utm_medium=partnership&utm_campaign=integration';

// A deliberate trading sign-in drops any stored return address (Asana 1220218671326301, Decision 3).
// sessionStorage survives the full-page navigation, so clearing before it works.
const clearStoredReturnTo = () => ImportSessionStorage.clearReturnTo();

// Importer deep link. Opens the in-app Import Cards modal on the Have List with the
// TopDecked option preselected (AddCards reads and strips the param). A signed-out
// visitor goes through ProtectedRoute, which stores this URL and returns them here
// after sign-in or signup. Plain anchor like the signup buttons.
const IMPORT_HREF = '/my-cards/have-list?import=topdecked';

// How TradeMagic works, in newcomer-friendly terms.
const STEPS: { title: string; body: string }[] = [
  {
    title: 'Bring your collection',
    body: 'Import the cards you already track in TopDecked. Your binder becomes a trade-ready list in less than a minute.',
  },
  {
    title: 'Auto-match with other traders',
    body: "Once your binder is on TradeMagic, we'll match you with people who want your cards.",
  },
  {
    title: 'Trade for value',
    body: 'Every card has a point value, where 100 points equals one US dollar. Trade your cards for points, then put those points toward the cards you want.',
  },
];

// Sample content for the hero summary panel and the binder list. These are
// illustrative numbers, NOT user data — both surfaces carry a visible "Example"
// label so a signed-out visitor cannot read them as their own collection.
const EXAMPLE_IMPORT = {
  headline: '1,241 cards imported from TopDecked',
  detail: '906 unique · 43 rows skipped',
  points: 148302,
};

// Have List preview under step 2. Card names render through CardLink, the same
// hover-preview component the blog uses, so the names resolve and link on their
// own — we supply the name only. Values go through the branded Points component.
//
// Point values are REAL, snapshotted from the live pricing source (pointValue on
// GET /cards/:scryfallId, i.e. market price x 100) on 2026-09-14, so the number
// beside each card actually corresponds to that card. They are a static snapshot
// rather than a live fetch — this is a marketing page and must render for
// signed-out visitors without an API round trip — so the panel carries the same
// "Example" label as the hero summary. Prices drift; re-snapshot if they look
// stale, and never invent a value here.
const EXAMPLE_HAVE_ROWS: { name: string; condition: string; points: number }[] = [
  { name: 'Goblin Guide', condition: 'NM', points: 156 },
  { name: 'Fury', condition: 'NM', points: 136 },
  { name: 'Slickshot Show-Off', condition: 'NM', points: 576 },
];

/** Real point value for the step-3 match card, same source and date as above. */
const MATCH_CARD_POINTS = 134; // Lava Spike (chk #178)

const TopDeckedPage: React.FC = () => {
  return (
    <>
      <SEOHelmet
        title="TradeMagic x TopDecked - Trade the Cards You Own"
        description="You cataloged your collection in TopDecked. Put it to work on TradeMagic: trade the cards you are not using for the ones you want, in a peer-to-peer points economy."
        canonical="https://trademagic.gg/topdecked"
        keywords="TradeMagic, TopDecked, MTG trading, Magic the Gathering trade, peer to peer card trading"
        type="website"
      />

      <BreadcrumbList
        breadcrumbs={[
          { name: 'Home', url: 'https://trademagic.gg/' },
          { name: 'TopDecked', url: 'https://trademagic.gg/topdecked' },
        ]}
      />

      <div className="td-page">
        {/* Co-brand lockup on the left, navigation on the right. The nav links
            collapse out at 640px and below, leaving the lockup only — there is
            deliberately no hamburger or drawer on this page. */}
        <header className="td-topbar">
          <div className="td-topbar-inner">
            <div className="td-lockup">
              <span className="td-brand">
                <img
                  className="td-brand-icon"
                  src={AssetUrls.logos.iconGold}
                  alt="TradeMagic"
                  width={32}
                  height={32}
                />
                <span className="td-brand-word">TradeMagic</span>
              </span>

              <span className="td-lockup-x" aria-hidden="true">
                &times;
              </span>

              <span className="td-brand">
                <TopDeckedMark size={32} className="td-brand-icon" />
                <span className="td-brand-word">TopDecked</span>
              </span>
            </div>

            <nav className="td-nav" aria-label="Page sections">
              <a className="td-nav-link" href="#how-it-works">
                How it works
              </a>
              <a className="td-nav-link" href="/login">
                Log in
              </a>
              {/* Secondary treatment: the hero's import CTA is the page's only
                  gradient button, so the header signup link stays quiet. */}
              <a className="td-btn td-nav-cta" href={SIGNUP_HREF} onClick={clearStoredReturnTo}>
                Get started
              </a>
            </nav>
          </div>
        </header>

        <main className="td-main">
          {/* Hero: copy left, card fan + example import panel right. */}
          <section className="td-hero">
            <div className="td-hero-copy">
              <span className="td-eyebrow">
                <TopDeckedMark size={18} className="td-eyebrow-mark" />
                TOPDECKED IMPORT — NOW LIVE
              </span>

              {/* Three lines per the mockup: the measure breaks "collection."
                  onto line two, and the explicit break keeps the gradient
                  sentence whole on line three rather than wrapping up beside
                  the black text. */}
              <h1 className="td-hero-title">
                You cataloged your collection.
                <br />
                <span
                  className="td-hero-title-accent"
                  style={{ backgroundImage: BRAND_GRADIENT }}
                >
                  Now put it to work.
                </span>
              </h1>

              <p className="td-hero-sub">
                Export your TopDecked collection, drop the CSV into TradeMagic,
                and mail cards you are not using for the ones you actually want.
              </p>

              <div className="td-cta-row">
                <a
                  className="td-btn td-btn-primary td-btn-with-mark"
                  href={IMPORT_HREF}
                  style={{ background: BRAND_GRADIENT }}
                >
                  <TopDeckedMark size={18} className="td-btn-mark" />
                  Import your TopDecked collection
                </a>
                <a className="td-btn td-btn-secondary" href="#how-it-works">
                  See how trading works
                </a>
              </div>

              {/* Separators are their own spans so the dots sit in real
                  whitespace instead of hugging the adjacent words. */}
              <p className="td-hero-note">
                <span>Free to join</span>
                <span className="td-hero-note-sep" aria-hidden="true">
                  ·
                </span>
                <span>Whole collection in one CSV</span>
                <span className="td-hero-note-sep" aria-hidden="true">
                  ·
                </span>
                <span>No listing fees</span>
              </p>
            </div>

            <div className="td-hero-visual" aria-hidden="true">
              <div className="td-card-fan">
                {/* Above the fold and the page's primary visual, so these load
                    eagerly — lazy-loading the hero delays first paint of the
                    thing the page is selling. The thumbnails further down stay
                    lazy. */}
                {HERO_CARD_IDS.map((id, index) => (
                  <img
                    key={id + index}
                    className={`td-fan-card td-fan-card-${index + 1}`}
                    src={AssetUrls.cards.getCardImage(id, 'normal')}
                    alt=""
                  />
                ))}
              </div>

              <div className="td-import-wrap">
                <span className="td-example-label">Example</span>
                <div className="td-import-panel">
                  <TopDeckedMark size={34} className="td-import-mark" />
                  <div className="td-import-col">
                    <p className="td-import-headline">
                      {EXAMPLE_IMPORT.headline}
                    </p>
                    <p className="td-import-detail">{EXAMPLE_IMPORT.detail}</p>
                  </div>
                  <div className="td-import-points">
                    <Points points={EXAMPLE_IMPORT.points} type="small" />
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* How it works. Two-column header: eyebrow + headline left,
              supporting copy right-aligned and bottom-aligned with it. */}
          <section className="td-section" id="how-it-works">
            <div className="td-section-head">
              <div className="td-section-head-left">
                <span className="td-section-eyebrow">HOW IT WORKS</span>
                <h2 className="td-section-title">
                  Three steps from binder to trade
                </h2>
              </div>
              <p className="td-section-head-right">
                Your TopDecked export becomes a trade-ready list, priced in
                points, matched against what other players are looking for.
              </p>
            </div>

            <div className="td-steps">
              {STEPS.map((step, index) => (
                <div className="td-step" key={step.title}>
                  <div className="td-step-head">
                    <span
                      className="td-step-num"
                      style={{ background: BRAND_GRADIENT }}
                    >
                      {index + 1}
                    </span>
                    <h3 className="td-step-title">{step.title}</h3>
                  </div>
                  <p className="td-step-body">{step.body}</p>

                  {/* Full-bleed inset panel previewing the UI each step leads
                      to. Static markup and CSS only — no authenticated-app
                      component is imported into this marketing page. */}
                  <div className="td-preview">
                    {index === 0 && (
                      <a className="td-preview-drop" href={IMPORT_HREF}>
                        <span className="td-preview-drop-title">
                          Drop your TopDecked CSV
                        </span>
                        <span className="td-preview-drop-sub">
                          .csv up to 20MB
                        </span>
                      </a>
                    )}

                    {index === 1 && (
                      <div className="td-have-table">
                        <div className="td-have-row td-have-head">
                          <span>NAME</span>
                          <span>COND</span>
                          <span>VALUE</span>
                        </div>
                        {EXAMPLE_HAVE_ROWS.map((row) => (
                          <div className="td-have-row" key={row.name}>
                            <span className="td-have-name">
                              <CardLink cardName={row.name} />
                            </span>
                            <span className="td-have-cond">
                              {row.condition}
                            </span>
                            <span className="td-have-value">
                              <Points points={row.points} type="small" />
                            </span>
                          </div>
                        ))}
                      </div>
                    )}

                    {index === 2 && (
                      <div className="td-match">
                        <span className="td-match-eyebrow">MATCH FOUND</span>
                        <div className="td-match-row">
                          <img
                            className="td-match-thumb"
                            src={AssetUrls.cards.getCardImage(
                              MATCH_CARD_ID,
                              'small'
                            )}
                            alt=""
                            loading="lazy"
                          />
                          <span className="td-match-text">
                            You send 1 &middot; NM
                          </span>
                          <span className="td-match-arrow" aria-hidden="true">
                            &rarr;
                          </span>
                          <Points points={MATCH_CARD_POINTS} type="small" />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Closing CTA */}
          <section className="td-closing">
            <div className="td-closing-panel">
              <div className="td-closing-lockup">
                <TopDeckedMark size={28} className="td-brand-icon" />
                <span className="td-lockup-x" aria-hidden="true">
                  &times;
                </span>
                <img
                  className="td-brand-icon"
                  src={AssetUrls.logos.iconGold}
                  alt="TradeMagic"
                  width={28}
                  height={28}
                />
              </div>

              <h2 className="td-closing-title">
                From export to trading in less than a minute
              </h2>
              <p className="td-closing-sub">
                Bring the collection you already track in TopDecked and get
                started trading today.
              </p>

              <div className="td-cta-row">
                <a
                  className="td-btn td-btn-primary td-btn-with-mark"
                  href={IMPORT_HREF}
                  style={{ background: BRAND_GRADIENT }}
                >
                  <TopDeckedMark size={18} className="td-btn-mark" />
                  Import your TopDecked collection
                </a>
                <a className="td-btn td-btn-secondary" href={SIGNUP_HREF} onClick={clearStoredReturnTo}>
                  Start trading on TradeMagic
                </a>
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default TopDeckedPage;
